import * as React from 'react';
import { TextField, Switch, FormControlLabel, Button, Checkbox } from "@mui/material";
import { useEffect, useRef, useState } from 'react';
import { ApiRequest, normalizePhoneCL, isValidRutCL } from "GlobalFunctions";
import LoadingButton from '@mui/lab/LoadingButton';
import Box from "elements/Box";
import AutocompleteField from 'elements/AutocompleteField';
import ImageUploader from "elements/ImageUploader";
import { useSnackbar } from 'notistack';
import * as pdfjsLib from "pdfjs-dist";
import { BsFiletypePdf } from "react-icons/bs";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const availabilityOptions = [
    'Disponibilidad completa',
    'Disponibilidad AM',
    'Disponibilidad PM',
    'Disponibilidad Fines de semana',
    'Disponibilidad según turno (clínico/hospitalario)',
];
const insuranceOptions = ['Fonasa', 'Particular', 'Reembolsable con Isapre'];

function PdfThumb({ src }: { src: string }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setError(false);
        (async () => {
            try {
                const pdf = await (pdfjsLib as any).getDocument({ url: src, withCredentials: false }).promise;
                if (cancelled) return;
                const page = await pdf.getPage(1);
                if (cancelled) return;
                const canvas = canvasRef.current;
                if (!canvas) return;
                const vp = page.getViewport({ scale: 1 });
                const scale = (canvas.parentElement?.clientWidth || 64) / vp.width;
                const scaled = page.getViewport({ scale });
                canvas.width = scaled.width;
                canvas.height = scaled.height;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;
                await page.render({ canvasContext: ctx, viewport: scaled }).promise;
            } catch {
                if (!cancelled) setError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [src]);
    if (error) return <BsFiletypePdf size={32} color="#005F6A" style={{ opacity: 0.7 }} />;
    return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
}

const defaultValues: any = {
    user_id: null,
    name: '',
    lastname: '',
    email: '',
    password: '',
    role_id: 1,
    phone: '',
    is_active: false,
    deleted: false,
    image: '',
    nurse_id: null,
    university_degree: '',
    service: '',
    price: '',
    region_id: '',
    locations: [],
    skills: [],
    advanced_wound_care: false,
    online_advice: false,
    about_me: '',
    whatsapp: '',
    badge_id: '',
    specialty_prices: {},
    availability: [],
    superintendencia_registro: '',
    rut: '',
    insurance_types: [],
    home_comuna_id: '',
    commission_consent: false,
    commission_consent_date: '',
}

export default function AccountEditor(props: any) {

    const [formValues, setFormValues] = useState<any>(defaultValues);
    const { enqueueSnackbar } = useSnackbar();
    const [loadingButton, setLoadingButton] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [whatsappError, setWhatsappError] = useState(false);
    const [rutError, setRutError] = useState(false);
    const [files, setFiles] = useState<any>([]);

    const [credentials, setCredentials] = useState<any[]>([]);
    const [credTitle, setCredTitle] = useState('');
    const [credFile, setCredFile] = useState<any>(null);
    const [credLoading, setCredLoading] = useState(false);
    const [commissionTerms, setCommissionTerms] = useState('');
    const [skillsList, setSkillsList] = useState<any[]>([]);
    const [acceptingCommission, setAcceptingCommission] = useState(false);
    const [missingFields, setMissingFields] = useState<string[]>([]);

    const missingFieldLabels: Record<string, string> = {
        image: 'Foto de perfil',
        rut: 'RUT',
        locations: 'Comunas de atención',
        whatsapp: 'WhatsApp',
        home_comuna_id: 'Comuna de residencia',
    };

    const loadDetails = async () => {
        await ApiRequest({
            url: '/users/details',
            method: 'GET',
            setResponse: (response: any) => {
                setFormValues({
                    ...defaultValues,
                    ...response,
                    skills: Array.isArray(response.skills) ? response.skills.map((s: any) => Number(s)) : [],
                });
                setMissingFields(response.missing_profile_fields || []);
            }
        });
    }

    useEffect(() => {
        loadDetails();
        ApiRequest({
            url: '/settings/details',
            method: 'GET',
            setResponse: (r: any) => { if (r.seo?.preferente_terms) setCommissionTerms(r.seo.preferente_terms); }
        });
        ApiRequest({
            url: '/skills/list',
            method: 'GET',
            query: { full_list: true },
            setResponse: (r: any) => { setSkillsList(r.list || []); }
        });
    }, []);

    useEffect(() => {
        if (formValues?.nurse_id) {
            loadCredentials(formValues.nurse_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues?.nurse_id]);

    /*=============================================
    UPDATE ACCOUNT
    =============================================*/
    const submit = async () => {
        const isEmailValid = !formValues.email || validateEmail(formValues.email);
        const isWhatsappValid = !formValues.whatsapp || validateWhatsApp(formValues.whatsapp);
        const isRutValid = !formValues.rut || isValidRutCL(formValues.rut);

        setEmailError(!isEmailValid);
        setWhatsappError(!isWhatsappValid);
        setRutError(!isRutValid);

        if (!isEmailValid) {
            enqueueSnackbar('El email ingresado no es válido', { variant: 'error' });
            return;
        }
        if (!isWhatsappValid) {
            enqueueSnackbar('El WhatsApp debe tener 9 dígitos (sin +56 ni espacios)', { variant: 'error' });
            return;
        }
        if (!isRutValid) {
            enqueueSnackbar('El RUT ingresado no es válido', { variant: 'error' });
            return;
        }

        const body = new FormData();
        body.append('formValues', JSON.stringify(formValues));
        if (files) body.append("image", files);

        setLoadingButton(true);
        await ApiRequest({
            url: '/users/updateAccount',
            method: 'POST',
            formData: true,
            body: body,
            setResponse: (response: any) => {
                setLoadingButton(false);
                if (response?.status === 'success') {
                    enqueueSnackbar('Perfil guardado correctamente', { variant: 'success' });
                    loadDetails();
                } else {
                    enqueueSnackbar(response?.message || 'Error al guardar', { variant: 'error' });
                }
            }
        });
    }

    const validateWhatsApp = (whatsapp: string) => /^\d{9}$/.test(whatsapp);
    const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

    /*=============================================
    COMISIÓN POR DERIVACIÓN ($10.000)
    =============================================*/
    const acceptCommission = async () => {
        setAcceptingCommission(true);
        await ApiRequest({
            url: '/kinesiologists/acceptCommission',
            method: 'POST',
            body: {},
            setResponse: (response: any) => {
                setAcceptingCommission(false);
                if (response?.status === 'success') {
                    enqueueSnackbar('Condiciones aceptadas. Ya puedes recibir contacto por WhatsApp.', { variant: 'success' });
                    loadDetails();
                } else {
                    enqueueSnackbar(response?.message || 'No se pudo registrar la aceptación', { variant: 'error' });
                }
            }
        });
    };

    /*=============================================
    ATENCIONES Y PRECIOS
    =============================================*/
    const updateSpecialtyPrice = (skill_id: number, price: string) => {
        const specialty_prices = { ...(formValues.specialty_prices || {}) };
        specialty_prices[String(skill_id)] = price;
        setFormValues({ ...formValues, specialty_prices });
    };

    /*=============================================
    CREDENCIALES
    =============================================*/
    const loadCredentials = async (nurse_id: any) => {
        if (!nurse_id) return;
        await ApiRequest({
            url: '/kinesiologists/credentials',
            method: 'GET',
            query: { nurse_id },
            setResponse: (r: any) => {
                if (r?.status === 'success') setCredentials(r.data || []);
            }
        });
    };

    const addCredential = async () => {
        if (!formValues?.nurse_id) { enqueueSnackbar('Primero guarda tu perfil profesional', { variant: 'warning' }); return; }
        if ((credentials?.length || 0) >= 3) { enqueueSnackbar('Solo puedes subir hasta 3 credenciales', { variant: 'warning' }); return; }
        if (!credTitle || !credFile) { enqueueSnackbar('Título y archivo son requeridos', { variant: 'warning' }); return; }
        const body = new FormData();
        body.append('formValues', JSON.stringify({ nurse_id: formValues.nurse_id, title: credTitle }));
        body.append('image', credFile);
        setCredLoading(true);
        await ApiRequest({
            url: '/kinesiologists/credentials',
            method: 'POST',
            formData: true,
            body,
            setResponse: (r: any) => {
                setCredLoading(false);
                if (r?.status === 'success') {
                    enqueueSnackbar(r.message || 'Certificado guardado correctamente', { variant: 'success' });
                    setCredTitle(''); setCredFile(null);
                    loadCredentials(formValues.nurse_id);
                } else {
                    enqueueSnackbar(r?.message || 'No se pudo guardar', { variant: 'error' });
                }
            }
        });
    };

    const deleteCredential = async (credential_id: any) => {
        await ApiRequest({
            url: '/kinesiologists/credentials/delete',
            method: 'POST',
            body: { credential_id },
            setResponse: (r: any) => {
                if (r?.status === 'success') {
                    enqueueSnackbar(r.message || 'Credencial eliminada', { variant: 'success' });
                    loadCredentials(formValues.nurse_id);
                } else {
                    enqueueSnackbar(r?.message || 'No se pudo eliminar', { variant: 'error' });
                }
            }
        });
    };

    return (
        <div className="content">
            <Box className='intent-auth_component'>

                {formValues.role_id === 2 && missingFields.length > 0 && (
                    <Box style={{ background: '#fffbeb', border: '1px solid #fbbf24', marginBottom: '16px' }}>
                        <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '4px' }}>
                            ⚠️ Tu perfil está incompleto
                        </div>
                        <div style={{ fontSize: '13px', color: '#78350f' }}>
                            Para aparecer en el buscador de pacientes te falta completar: <b>{missingFields.map(f => missingFieldLabels[f] || f).join(', ')}</b>.
                        </div>
                    </Box>
                )}

                {/* ── IMAGEN + DATOS BÁSICOS ── */}
                <div className='section_flex'>
                    <ImageUploader
                        className="user_image"
                        image={formValues.image}
                        setImage={(newImage: any) => setFormValues({ ...formValues, image: newImage })}
                        setFiles={setFiles}
                    />

                    <div className='account_right'>
                        <div className='input_group'>
                            <div className='input_row'>
                                <TextField
                                    label="Nombre"
                                    value={formValues.name}
                                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                                <TextField
                                    label="Apellido"
                                    value={formValues.lastname}
                                    onChange={(e) => setFormValues({ ...formValues, lastname: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                            </div>

                            <AutocompleteField
                                label="Region"
                                endpoint="/locations/regiones/list"
                                value={formValues.region_id}
                                onChange={(data: any) => setFormValues({ ...formValues, region_id: data.region_id })}
                            />

                            {formValues.commission_consent &&
                                <AutocompleteField
                                    label="Distintivo"
                                    endpoint="/badges/list"
                                    value={formValues.badge_id}
                                    onChange={(data: any) => setFormValues({ ...formValues, badge_id: data.badge_id })}
                                />
                            }
                        </div>
                    </div>
                </div>

                <div className="input_group">

                    {/* ── CONTACTO ── */}
                    <div className='input_row'>
                        <TextField
                            label="Número de Teléfono"
                            value={formValues.phone}
                            onChange={(e) => {
                                const updates: any = { phone: e.target.value };
                                if (!formValues.whatsapp) updates.whatsapp = normalizePhoneCL(e.target.value);
                                setFormValues({ ...formValues, ...updates });
                                if (!formValues.whatsapp) setWhatsappError(false);
                            }}
                            variant="outlined" fullWidth
                            helperText={!formValues.whatsapp ? 'Se usará también como WhatsApp' : ''}
                        />
                        <TextField
                            label="Correo Electrónico"
                            value={formValues.email}
                            onChange={(e) => {
                                setFormValues({ ...formValues, email: e.target.value });
                                setEmailError(!validateEmail(e.target.value) && e.target.value !== '');
                            }}
                            error={emailError}
                            helperText={emailError ? 'Correo inválido' : ''}
                            variant="outlined" fullWidth
                        />
                        <TextField
                            label="WhatsApp"
                            value={formValues.whatsapp}
                            onChange={(e) => {
                                const normalized = normalizePhoneCL(e.target.value);
                                setFormValues({ ...formValues, whatsapp: normalized });
                                setWhatsappError(!validateWhatsApp(normalized) && normalized !== '');
                            }}
                            error={whatsappError}
                            helperText={
                                whatsappError ? 'WhatsApp inválido (9 dígitos)'
                                    : !formValues.commission_consent ? 'Se mostrará a pacientes solo si aceptas la comisión por derivación (más abajo)'
                                        : 'Este número es el que verán los pacientes para contactarte'
                            }
                            variant="outlined" fullWidth
                        />
                    </div>

                    {/* ── UBICACIÓN DE ATENCIÓN ── */}
                    {formValues.region_id && formValues.region_id !== '' && (
                        <AutocompleteField
                            label="Comunas de atención"
                            endpoint="/locations/comunas/list"
                            endpoint_params={{ region_id: formValues.region_id }}
                            multiple={true}
                            value={formValues.locations}
                            onChange={(data: any) => setFormValues({ ...formValues, locations: data ? data : [] })}
                        />
                    )}

                    {/* ── RUT ── */}
                    <div className='input_row'>
                        <TextField
                            label="RUT"
                            value={formValues.rut}
                            onChange={(e) => {
                                setFormValues({ ...formValues, rut: e.target.value });
                                setRutError(e.target.value !== '' && !isValidRutCL(e.target.value));
                            }}
                            error={rutError}
                            helperText={rutError ? 'RUT inválido' : ''}
                            variant="outlined" fullWidth
                        />
                    </div>

                    {/* ── REGISTRO SUPERINTENDENCIA + COMUNA RESIDENCIA ── */}
                    <div className='input_row'>
                        <TextField
                            label="Registro de la Superintendencia de Salud"
                            value={formValues.superintendencia_registro}
                            onChange={(e) => setFormValues({ ...formValues, superintendencia_registro: e.target.value })}
                            variant="outlined" fullWidth
                        />
                        <AutocompleteField
                            label="Comuna de residencia"
                            endpoint="/locations/comunas/list"
                            value={formValues.home_comuna_id}
                            onChange={(data: any) => setFormValues({ ...formValues, home_comuna_id: data.comuna_id })}
                        />
                    </div>

                    {/* ── ATENCIONES Y PRECIOS ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 4px 0' }}>Especialidades y Atenciones</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', opacity: 0.75 }}>
                            Selecciona tus especialidades e indica un valor referencial para cada una.
                        </p>

                        <AutocompleteField
                            label="Especialidades"
                            endpoint="/skills/list"
                            renderValues={["label"]}
                            multiple={true}
                            value={formValues.skills}
                            onChange={(data: any) => setFormValues({ ...formValues, skills: data ? data : [] })}
                        />

                        {(formValues.skills || []).length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                                {(formValues.skills || []).map((skill_id: number, i: number, arr: number[]) => {
                                    const label = skillsList.find((s: any) => s.skill_id === skill_id)?.skill || `Especialidad #${skill_id}`;
                                    const isLast = i === arr.length - 1;
                                    return (
                                        <div
                                            key={skill_id}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
                                                paddingBottom: isLast ? 0 : '12px',
                                                marginBottom: isLast ? 0 : '12px',
                                                borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                                            }}
                                        >
                                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{label}</span>
                                            <TextField
                                                label="Valor referencial ($)"
                                                value={formValues.specialty_prices?.[String(skill_id)] || ''}
                                                onChange={(e) => updateSpecialtyPrice(skill_id, e.target.value.replace(/[^0-9]/g, ''))}
                                                variant="outlined"
                                                size="small"
                                                style={{ width: '200px' }}
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Box>

                    {/* ── DISPONIBILIDAD INTERNA ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 8px 0' }}>Disponibilidad (uso interno)</h3>
                        {availabilityOptions.map((option) => (
                            <FormControlLabel
                                key={option}
                                control={
                                    <Checkbox
                                        checked={(formValues.availability || []).includes(option)}
                                        onChange={(e) => {
                                            const availability = e.target.checked
                                                ? [...(formValues.availability || []), option]
                                                : (formValues.availability || []).filter((x: string) => x !== option);
                                            setFormValues({ ...formValues, availability });
                                        }}
                                    />
                                }
                                label={option}
                                style={{ display: 'flex' }}
                            />
                        ))}
                    </Box>

                    {/* ── TIPO DE ATENCIÓN / PAGO ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 8px 0' }}>Tipo de atención / pago</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {insuranceOptions.map((option) => (
                                <FormControlLabel
                                    key={option}
                                    control={
                                        <Checkbox
                                            checked={(formValues.insurance_types || []).includes(option)}
                                            onChange={(e) => {
                                                const insurance_types = e.target.checked
                                                    ? [...(formValues.insurance_types || []), option]
                                                    : (formValues.insurance_types || []).filter((x: string) => x !== option);
                                                setFormValues({ ...formValues, insurance_types });
                                            }}
                                        />
                                    }
                                    label={option}
                                />
                            ))}
                        </div>
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.advanced_wound_care}
                                onChange={(e) => setFormValues({ ...formValues, advanced_wound_care: e.target.checked })}
                            />
                        }
                        label="Kinesiología a Domicilio"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.online_advice}
                                onChange={(e) => setFormValues({ ...formValues, online_advice: e.target.checked })}
                            />
                        }
                        label="Kinesiología en Consulta"
                    />

                    {/* ── COMISIÓN POR DERIVACIÓN ($10.000) ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 8px 0' }}>Comisión por Derivación ($10.000)</h3>

                        {formValues.commission_consent ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0A7715' }}>
                                <CheckCircleIcon fontSize="small" />
                                <span>
                                    Aceptaste el cobro de $10.000 por derivación
                                    {formValues.commission_consent_date ? ` el ${String(formValues.commission_consent_date).slice(0, 10)}` : ''}.
                                    Tu perfil muestra contacto directo por WhatsApp.
                                </span>
                            </div>
                        ) : (
                            <>
                                <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#555' }}>
                                    Actualmente tu perfil solo permite mensajería interna. Si aceptas el cobro de $10.000 por derivación,
                                    tu perfil mostrará un botón de contacto directo por WhatsApp en lugar de mensajería interna.
                                </p>
                                <div style={{ padding: '14px 16px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '13px', color: '#78350f', lineHeight: 1.6, marginBottom: '10px' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '6px', color: '#92400e' }}>Condiciones de Cobro por Derivación ($10.000)</div>
                                    <div dangerouslySetInnerHTML={{ __html: commissionTerms }} />
                                </div>
                                <LoadingButton
                                    size="small" variant="contained"
                                    onClick={acceptCommission}
                                    loading={acceptingCommission}
                                >
                                    Aceptar condiciones y activar WhatsApp
                                </LoadingButton>
                            </>
                        )}
                    </Box>

                    {/* ── SOBRE MÍ + ESTADO ── */}
                    <div>
                        <TextField
                            label="Sobre mí"
                            value={formValues.about_me}
                            onChange={(e) => setFormValues({ ...formValues, about_me: e.target.value })}
                            variant="outlined" fullWidth multiline rows={4}
                            placeholder="Cuéntale a los pacientes quién eres: tu experiencia, especialización, años en el área, tipo de atenciones que realizas y cualquier detalle que te diferencie. Un perfil completo genera más confianza y aumenta las posibilidades de que te elijan."
                        />
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 2px' }}>
                            💡 Los pacientes leen esto antes de contactarte. Cuanto más completo, mayor confianza generas.
                        </p>
                    </div>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.is_active}
                                onChange={(e) => setFormValues({ ...formValues, is_active: e.target.checked })}
                            />
                        }
                        label="Perfil activo"
                    />

                    {/* ── CREDENCIALES ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 4px 0' }}>Certificados y Especializaciones</h3>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#475569' }}>
                            Sube tus <b>diplomados, cursos o magíster</b> relacionados con kinesiología.
                        </p>
                        <div style={{ margin: '0 0 12px 0', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                            ✅ Los kinesiólogos con certificados verificados tienen <b>mayor visibilidad</b> y son elegidos con más frecuencia por los pacientes. Puedes subir hasta 3 documentos (imagen o PDF).
                        </div>

                        {!formValues?.nurse_id ? (
                            <p style={{ fontSize: '13px', opacity: 0.6 }}>
                                Primero guarda tu perfil profesional para poder subir credenciales.
                            </p>
                        ) : (
                            <>
                                <div className="input_row">
                                    <TextField
                                        label="Título de la credencial"
                                        value={credTitle}
                                        onChange={(e) => setCredTitle(e.target.value)}
                                        variant="outlined" fullWidth
                                    />
                                    <Button variant="outlined" component="label" sx={{ minWidth: '210px', height: '56px' }}>
                                        {credFile?.name ? credFile.name.slice(0, 22) + (credFile.name.length > 22 ? '…' : '') : 'Seleccionar archivo'}
                                        <input
                                            type="file" hidden accept="image/*,.pdf,application/pdf"
                                            onChange={(e: any) => {
                                                const f = e?.target?.files?.[0];
                                                if (f) setCredFile(f);
                                            }}
                                        />
                                    </Button>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                                    <LoadingButton
                                        size="small" variant="contained"
                                        onClick={addCredential}
                                        loading={credLoading}
                                        disabled={(credentials?.length || 0) >= 3 || credLoading}
                                    >
                                        Agregar certificado
                                    </LoadingButton>
                                    <span style={{ fontSize: '12px', opacity: 0.6 }}>
                                        {credentials?.length || 0}/3
                                    </span>
                                </div>

                                {credentials && credentials.length > 0 && (
                                    <div style={{ marginTop: '12px', display: 'grid', gap: '10px' }}>
                                        {credentials.map((c: any) => (
                                            <div key={c.credential_id} style={{ display: 'flex', gap: '12px', alignItems: 'center', border: '1px solid #e6e6e6', borderRadius: '10px', padding: '10px' }}>
                                                <div style={{ width: '64px', height: '64px', borderRadius: '8px', overflow: 'hidden', background: '#f5f5f5', flex: '0 0 64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {c.image && (c.image.toLowerCase().endsWith('.pdf')
                                                        ? <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                                                            <PdfThumb src={c.image} />
                                                          </a>
                                                        : <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                                                            <img src={c.image} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                          </a>
                                                    )}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600 }}>{c.title}</div>
                                                    <div style={{ fontSize: '12px', opacity: 0.65 }}>{c.created_at || ''}</div>
                                                </div>
                                                <Button variant="text" color="error" size="small" onClick={() => deleteCredential(c.credential_id)}>
                                                    Eliminar
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </Box>

                    <LoadingButton
                        size="small" type="submit" fullWidth variant="contained"
                        sx={{ fontSize: '13px', padding: '10px 0px' }}
                        onClick={submit} loading={loadingButton}
                    >
                        Guardar
                    </LoadingButton>

                </div>

            </Box>
        </div>
    );
}
